import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Skeleton from "react-loading-skeleton";
import Button from "core/components/Button";
import Field from "core/components/Field";
import Flex from "core/components/Flex";
import {
  Panel,
  PanelContent,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from "core/components/Panel";
import Tabs from "core/components/Tabs/Tabs";
import Typography from "core/components/Typography";
import { useMembershipListQuery } from "core/queries";
import {
  updateMemberships,
  deleteMemberships,
  disableIndividualCredentialById,
} from "modules/admin/actions";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";

import {
  DEFAULT_USER,
  ROLES,
  TABLEAU_ROLES,
  ANALYTICS_HUB_USER_ROLES,
  SYSTEM_ROLE_KEYS,
} from "core/constants";
import useForm from "core/hooks/useForm";
import z from "zod";
import RemoveMemberConfirmModal from "./RemoveMemberConfirmModal";
import Navigate from "core/components/Navigate";
import ConfirmModal from "core/components/ConfirmModal";
import Error from "core/components/Error";
import { toast } from "sonner";

const membershipSchema = z.object({
  user_id: z.string(),
  organization_id: z.string(),
  role: z.enum(Object.keys(ROLES)),
  analytics_hub_access: z
    .enum(Object.keys(ANALYTICS_HUB_USER_ROLES))
    .default("none"),
  tableau_config: z.object({
    license: z.enum(Object.keys(TABLEAU_ROLES)).default("none"),
    enabled: z.boolean(),
  }),
  direct_data_access_enabled: z.boolean(),
});

const EMPTY_MEMBERSHIP = {
  user_id: "",
  organization_id: "",
  role: "orgUser",
  analytics_hub_access: "none",
  tableau_config: {
    license: "none",
    enabled: false,
  },
  direct_data_access_enabled: false,
};

const GeneralForm = ({ membership, isLoading, closePanel }) => {
  const queryClient = useQueryClient();

  const [isConfirmRemoveModalOpen, setConfirmRemoveModalOpen] = useState(false);

  const { formValues, isDisabled, onFieldChange } = useForm({
    placeholderValues: membership,
    initialValues: membership,
    formSchema: membershipSchema,
    shouldValidateWithSchema: true,
  });
  const updateMutationFn = useAuthenticatedMutation(updateMemberships);
  const { mutate: saveMembership, isPending: isSaving } = useMutation({
    mutationFn: updateMutationFn,
    onError: () => {
      toast.error("Failed to update member");
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["user-profiles", { userId: membership.user_id }],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "memberships",
          { userId: membership.user_id, orgId: membership.organization_id },
        ],
      });
      toast.success("Member updated successfully");
    },
  });

  const handleSave = () => {
    const preparedUpdate = membershipSchema.safeParse(formValues).data;

    saveMembership({ memberships: [preparedUpdate] });
  };

  const roleOptions = useMemo(() => {
    return Object.entries(ROLES)
      .filter(
        ([key]) =>
          membership.organization_id === "populi" ||
          !SYSTEM_ROLE_KEYS.includes(key)
      )
      .map(([key, value]) => ({ label: value, value: key }));
  }, [membership.organization_id]);

  const ahuRoleOptions = useMemo(() => {
    return Object.entries(ANALYTICS_HUB_USER_ROLES).map(([key, value]) => ({
      label: value,
      value: key,
    }));
  }, []);

  const biRoleOptions = useMemo(() => {
    return Object.entries(TABLEAU_ROLES)
      .filter(
        ([key]) => membership.organization_id === "populi" || key !== "creator"
      )
      .map(([key, value]) => ({ label: value, value: key }));
  }, [membership.organization_id]);

  return (
    <>
      <Flex direction="column" gap="4px">
        <Field
          id="role"
          type="combobox"
          labelText="Role"
          isRequired
          showSkeleton={isLoading}
          isDisabled={isSaving}
          value={formValues.role}
          onChange={onFieldChange("role")}
          options={roleOptions}
        />
        <Field
          id="analyticsHubUser"
          type="combobox"
          labelText="Analytics Hub User"
          isRequired
          showSkeleton={isLoading}
          isDisabled={isSaving}
          value={formValues.analytics_hub_access ?? "none"}
          onChange={onFieldChange("analytics_hub_access")}
          options={ahuRoleOptions}
        />
        <Field
          id="biPortalUser"
          type="combobox"
          labelText="BI Portal User"
          isRequired
          showSkeleton={isLoading}
          isDisabled={isSaving}
          value={formValues.tableau_config.license}
          onChange={(value) => {
            onFieldChange("tableau_config.license")(value);
            onFieldChange("tableau_config.enabled")(value !== "none");
          }}
          options={biRoleOptions}
        />
        <Field
          id="directDataAccess"
          type="radio"
          labelText="Direct Data Access"
          hideError
          isDisabled={isSaving}
          value={formValues.direct_data_access_enabled}
          onChange={onFieldChange("direct_data_access_enabled")}
          options={[
            { label: "Enabled", value: true },
            { label: "Disabled", value: false },
          ]}
        />
        <Flex direction="row" gap="16px" justifyContent="flex-end">
          <Button
            variant="outlined"
            disabled={isLoading}
            onClick={() => setConfirmRemoveModalOpen(true)}
          >
            Remove Member
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isLoading || isDisabled}
            isLoading={isSaving}
          >
            Save Changes
          </Button>
        </Flex>
      </Flex>
      <RemoveMemberConfirmModal
        member={membership}
        isOpen={isConfirmRemoveModalOpen}
        close={() => {
          setConfirmRemoveModalOpen(false);
        }}
        onSuccess={() => {
          setConfirmRemoveModalOpen(false);
          closePanel();
        }}
      />
    </>
  );
};

const DataCredentialsForm = ({ membership, userId, orgId }) => {
  const queryClient = useQueryClient();

  const [showDisableConfirmModal, setShowDisableConfirmModal] = useState(false);
  const [error, setError] = useState(null);

  const disableMutationFn = useAuthenticatedMutation(
    disableIndividualCredentialById
  );
  const { mutate: disableCredential, isPending: isDisabling } = useMutation({
    mutationFn: disableMutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["memberships", { userId, orgId }],
      });
      setShowDisableConfirmModal(false);
      toast.success("Credential disabled successfully");
    },
    onError: (error) => {
      console.log({ error });
      setError("Failed to disable credential");
    },
  });

  const credential = Object.values(membership?.data_access_info ?? {}).find(
    (c) => c.active
  );

  const downloadDate = credential
    ? credential.generated_timestamp
      ? new Date(credential.generated_timestamp).toLocaleDateString()
      : "Unknowon"
    : "—";
  const daysToExpire = credential ? credential.days_to_expire : "—";

  return (
    <Flex direction="column" gap="16px">
      <Flex direction="column" gap="16px">
        <Flex direction="column" gap="4px">
          <Typography variant="h6" noMargin>
            Downloaded On
          </Typography>
          <Typography variant="body" noMargin>
            {downloadDate}
          </Typography>
        </Flex>
        <Flex direction="column" gap="4px">
          <Typography variant="h6" noMargin>
            Expiration
          </Typography>
          <Typography variant="body" noMargin>
            {credential ? `In ${daysToExpire} days` : "—"}
          </Typography>
        </Flex>
        {credential && (
          <Flex justifyContent="flex-end" margin="24px 0 0 0">
            <Button
              variant="outlined"
              width="auto"
              onClick={() => {
                setShowDisableConfirmModal(true);
              }}
            >
              Disable Current
            </Button>
          </Flex>
        )}
      </Flex>
      <ConfirmModal
        dataTest="disable-member-credential-modal"
        visible={showDisableConfirmModal}
        onNo={() => setShowDisableConfirmModal(false)}
        dismiss={() => setShowDisableConfirmModal(false)}
        noLabel="Cancel"
        onYes={() => {
          disableCredential({
            userId,
            organizationId: orgId,
            credentialId: credential.credential_id,
          });
        }}
        isSubmitting={isDisabling}
        title="Disable Individual Credential?"
      >
        <Error>{error}</Error>
        <Typography variant="p" style={{ maxWidth: "500px" }}>
          Disabling this credential will cause it to immediately expire. Do you
          still want to continue?
        </Typography>
      </ConfirmModal>
    </Flex>
  );
};

const MemberDetailsPanel = () => {
  const { userId, orgId } = useParams();
  const [isOpen, setOpen] = useState(true);

  const {
    data: membershipWithProfile,
    isLoading,
    isPlaceholderData,
    isFetching,
  } = useMembershipListQuery({
    userId,
    orgId,
    select: (data) => {
      if (data.rows.length >= 1) {
        return data.rows[0];
      }

      return EMPTY_MEMBERSHIP;
    },
  });

  const isLoadingProfile = isLoading || (isPlaceholderData && isFetching);

  if (!isLoadingProfile && !membershipWithProfile) {
    return <Navigate to="../" />;
  }

  return (
    <Panel isOpen={isOpen} onOpenChange={setOpen} onCloseRedirectTo="../">
      <div style={{ display: "flex", gap: "15px", width: "100%" }}>
        <FontAwesomeIcon
          icon={faUser}
          style={{
            width: "28px",
            height: "32px",
            padding: "9px 12px",
            color: "#fff",
            backgroundColor: "#00C1F3",
            borderRadius: "5px",
          }}
        />
        <PanelHeader>
          {isLoadingProfile ? (
            <>
              <Skeleton width={180} height={22} />
              <Skeleton width={220} height={16} style={{ marginTop: 4 }} />
            </>
          ) : (
            <>
              <PanelTitle>
                {membershipWithProfile?.name || "Unknown User"}
              </PanelTitle>

              <PanelDescription>
                {membershipWithProfile?.email || ""}
              </PanelDescription>

              {membershipWithProfile?.alternate_email && (
                <PanelDescription>
                  {membershipWithProfile.alternate_email}
                </PanelDescription>
              )}
            </>
          )}
        </PanelHeader>
      </div>
      <PanelContent>
        <Tabs
          type="content"
          basis="full"
          items={[
            {
              id: "general",
              title: "General",
              disabled: isLoadingProfile,
              content: (
                <GeneralForm
                  membership={membershipWithProfile}
                  isLoading={isLoadingProfile}
                  closePanel={() => setOpen(false)}
                />
              ),
            },
            {
              id: "data-credentials",
              title: "Data Credentials",
              disabled:
                isLoadingProfile ||
                !membershipWithProfile?.direct_data_access_enabled,
              content: (
                <DataCredentialsForm
                  membership={membershipWithProfile}
                  userId={userId}
                  orgId={orgId}
                />
              ),
            },
          ]}
        />
      </PanelContent>
    </Panel>
  );
};

export default MemberDetailsPanel;
